import React, { useCallback, useEffect, useMemo } from "react";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { compose } from "redux";
import {
    EcheqsMassivesManagementBatchDetail,
    EcheqsMassivesManagementBatchDetailMobileDownloadAction,
} from "@ui-kit/components/index.es";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import {
    actions as echeqMassManagementActions,
    selectors as echeqMassManagementSelectors,
} from "reducers/echeqMassManagement";
import { actions as statusActions } from "reducers/status";
import * as i18n from "util/i18n";
import {
    getEcheqBatchDetailReturnToFromLocation,
    isUxDashboardImprovementsEnabled,
    useEmbeddedV3ShellLayoutSync,
} from "util/v3ShellLayoutSync";
import { getPostLoginHomePath } from "util/v3UxConfig";

import { ECHEQS_MASS_MANAGEMENT_LIST_PATH } from "./echeqMassManageConstants";
import {
    mapBatchHeaderToGeneralData,
    mapBatchHeaderToStatusTag,
    mapBatchHeaderToSummary,
    mapManagementActionToUi,
    resolveMassEcheqManageBatchDetailTitle,
} from "./echeqMassManageBatchDetailUtils";
import { createI18nLabelResolver, createPaginatedLoadHandlers } from "./echeqMassManagePageUtils";
import { DETAIL_SCOPE_BATCH } from "./echeqMassManageUtils";

const FORM_ID = "echeqs.massives.manage.batchdetail";

function EcheqsMassManageBatchDetailPage({
    dispatch,
    isDesktop,
    embeddedInV3Shell,
    match,
    location,
    batchDetailHeader,
    batchDetailList,
    detailPageNumberRedux,
    detailTotalPages,
    detailStatusFilter,
    fetchingDetail,
    fetchingBatchDownload,
    batchDetailItemReceipt,
    fetchingItemReceipt,
    fetchingPendingTicket,
}) {
    const idBatch = match?.params?.idBatch;

    const getLabels = useMemo(() => createI18nLabelResolver(), []);

    const goBackPath = useMemo(
        () => getEcheqBatchDetailReturnToFromLocation(location) || ECHEQS_MASS_MANAGEMENT_LIST_PATH,
        [location],
    );

    const activeStatusFilter = detailStatusFilter || "ALL";

    const loadDetail = useCallback(
        (pageNumber, nextStatusFilter = activeStatusFilter) => {
            if (!idBatch) {
                return;
            }
            dispatch(
                echeqMassManagementActions.detailRequest(idBatch, pageNumber, isDesktop, {
                    detailScope: DETAIL_SCOPE_BATCH,
                    statusFilter: nextStatusFilter,
                }),
            );
        },
        [activeStatusFilter, dispatch, idBatch, isDesktop],
    );

    useEffect(() => {
        if (!idBatch) {
            dispatch(push(ECHEQS_MASS_MANAGEMENT_LIST_PATH));
            return undefined;
        }
        if (location) {
            dispatch(statusActions.saveLastHref(location));
        }
        dispatch(echeqMassManagementActions.resetBatchDetailFilters());
        dispatch(
            echeqMassManagementActions.detailRequest(idBatch, 1, isDesktop, {
                detailScope: DETAIL_SCOPE_BATCH,
                statusFilter: "ALL",
            }),
        );
        return () => {
            dispatch(echeqMassManagementActions.clearItemReceipt());
            dispatch(echeqMassManagementActions.clearReceiptCache());
            dispatch(echeqMassManagementActions.resetBatchDetailFilters());
        };
    }, [dispatch, idBatch, isDesktop]);

    const handleGoBack = useCallback(() => {
        dispatch(echeqMassManagementActions.resetBatchDetailFilters());
        dispatch(push(goBackPath));
    }, [dispatch, goBackPath]);

    const handleGoToHome = useCallback(() => {
        dispatch(push(getPostLoginHomePath(isUxDashboardImprovementsEnabled())));
    }, [dispatch]);

    const handleGoToHistory = useCallback(() => {
        dispatch(push("/transactions/list"));
    }, [dispatch]);

    const handleStatusFilterChange = useCallback(
        (value) => {
            loadDetail(1, value);
        },
        [loadDetail],
    );

    const { paginationAction } = useMemo(
        () =>
            createPaginatedLoadHandlers({
                isDesktop,
                currentPage: detailPageNumberRedux,
                onLoadPage: loadDetail,
            }),
        [detailPageNumberRedux, isDesktop, loadDetail],
    );

    const handleDownloadExcel = useCallback(() => {
        if (!idBatch || fetchingBatchDownload) {
            return;
        }
        dispatch(echeqMassManagementActions.batchDownloadRequest(idBatch));
    }, [dispatch, fetchingBatchDownload, idBatch]);

    const handleItemClick = useCallback(
        (item) => {
            if (fetchingPendingTicket || fetchingItemReceipt) {
                return;
            }
            if (item?.status === "PENDING" && location) {
                dispatch(statusActions.saveLastHref(location));
            }
            dispatch(echeqMassManagementActions.itemReceiptRequest(item));
        },
        [dispatch, fetchingItemReceipt, fetchingPendingTicket, location],
    );

    const handleDownloadReceipt = useCallback(
        (item) => {
            dispatch(echeqMassManagementActions.itemReceiptPdfRequest(item, dispatch));
        },
        [dispatch],
    );

    const handleReceiptClose = useCallback(() => {
        dispatch(echeqMassManagementActions.clearItemReceipt());
    }, [dispatch]);

    const statusTag = useMemo(() => mapBatchHeaderToStatusTag(batchDetailHeader, getLabels), [
        batchDetailHeader,
        getLabels,
    ]);

    const showDownloadExcel = useMemo(() => {
        const batchStatus = statusTag?.status;
        return Boolean(batchStatus) && batchStatus !== "PROCESSING";
    }, [statusTag?.status]);

    const mobileShellRightContent = useMemo(() => {
        if (isDesktop || !showDownloadExcel) {
            return undefined;
        }

        return (
            <EcheqsMassivesManagementBatchDetailMobileDownloadAction
                isDownloading={fetchingBatchDownload}
                onDownload={handleDownloadExcel}
                ariaLabel={i18n.get(`${FORM_ID}.downloadExcel`)}
            />
        );
    }, [fetchingBatchDownload, handleDownloadExcel, isDesktop, showDownloadExcel]);

    const title = useMemo(() => (idBatch ? resolveMassEcheqManageBatchDetailTitle(idBatch, getLabels) : ""), [
        idBatch,
        getLabels,
    ]);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        headerTitle: !isDesktop ? title : undefined,
        goBackPath,
        scopes: ["echeqs"],
        rightContentMobile: mobileShellRightContent,
    });

    const generalData = useMemo(() => mapBatchHeaderToGeneralData(batchDetailHeader, getLabels), [
        batchDetailHeader,
        getLabels,
    ]);

    const summary = useMemo(() => mapBatchHeaderToSummary(batchDetailHeader), [batchDetailHeader]);

    const data = useMemo(
        () => (fetchingPendingTicket ? [] : batchDetailList || []),
        [batchDetailList, fetchingPendingTicket],
    );

    const managementAction = useMemo(() => mapManagementActionToUi(batchDetailHeader?.action), [
        batchDetailHeader?.action,
    ]);

    const totalPages = useMemo(() => Math.max(1, detailTotalPages || 1), [detailTotalPages]);

    const paginationProps = useMemo(
        () => ({
            pageNumber: detailPageNumberRedux,
            totalPages,
            action: paginationAction,
        }),
        [detailPageNumberRedux, paginationAction, totalPages],
    );

    if (!idBatch) {
        return null;
    }

    const hideMobileLegacyHeader = embeddedInV3Shell && isUxDashboardImprovementsEnabled() && !isDesktop;

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="echeqs" />}
            {!hideMobileLegacyHeader && !isDesktop && (
                <div className="admin-detail-head px-0 mb-0">
                    <Head
                        titleText={title}
                        rightContent={() => mobileShellRightContent}
                        isFetchingExport={fetchingBatchDownload}
                        onBack={handleGoBack}
                        titleClassName="header-mobile-title-background-blue white-space-normal align-center"
                        headerClassName="blue-main-header-mobile servive-payment-title-mobile"
                    />
                </div>
            )}
            <EcheqsMassivesManagementBatchDetail
                goBack={handleGoBack}
                title={title}
                statusTag={statusTag}
                fileName={batchDetailHeader?.fileName}
                onDownloadExcel={showDownloadExcel ? handleDownloadExcel : undefined}
                isDownloadingExcel={fetchingBatchDownload}
                suppressMobileHeader={!isDesktop}
                managementAction={managementAction}
                generalData={generalData}
                summary={summary}
                data={data}
                getLabels={getLabels}
                statusFilter={activeStatusFilter}
                onStatusFilterChange={handleStatusFilterChange}
                paginationProps={paginationProps}
                loading={fetchingDetail || fetchingPendingTicket}
                endMessageMobileScroll={i18n.get(`${FORM_ID}.emptyState`)}
                onGoToHome={handleGoToHome}
                onGoToHistory={handleGoToHistory}
                onItemClick={handleItemClick}
                onDownloadReceipt={handleDownloadReceipt}
                selectedReceipt={batchDetailItemReceipt}
                fetchingReceipt={fetchingItemReceipt}
                onReceiptClose={handleReceiptClose}
            />
        </>
    );
}

EcheqsMassManageBatchDetailPage.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    embeddedInV3Shell: bool,
    match: shape({
        params: shape({
            idBatch: string,
        }),
    }).isRequired,
    location: shape({}),
    batchDetailHeader: shape({}),
    batchDetailList: arrayOf(shape({})),
    detailPageNumberRedux: number,
    detailTotalPages: number,
    detailStatusFilter: string,
    fetchingDetail: bool,
    fetchingBatchDownload: bool,
    batchDetailItemReceipt: shape({}),
    fetchingItemReceipt: bool,
    fetchingPendingTicket: bool,
};

EcheqsMassManageBatchDetailPage.defaultProps = {
    embeddedInV3Shell: false,
    location: null,
    batchDetailHeader: null,
    batchDetailList: [],
    detailPageNumberRedux: 1,
    detailTotalPages: 1,
    detailStatusFilter: "ALL",
    fetchingDetail: false,
    fetchingBatchDownload: false,
    batchDetailItemReceipt: null,
    fetchingItemReceipt: false,
    fetchingPendingTicket: false,
};

const mapStateToProps = (state) => ({
    batchDetailHeader: echeqMassManagementSelectors.getBatchDetailHeader(state),
    batchDetailList: echeqMassManagementSelectors.getBatchDetailList(state),
    detailPageNumberRedux: echeqMassManagementSelectors.getDetailPageNumber(state),
    detailTotalPages: echeqMassManagementSelectors.getDetailTotalPages(state),
    detailStatusFilter: echeqMassManagementSelectors.getDetailStatusFilter(state),
    fetchingDetail: echeqMassManagementSelectors.getFetchingDetail(state),
    fetchingBatchDownload: echeqMassManagementSelectors.getFetchingBatchDownload(state),
    batchDetailItemReceipt: echeqMassManagementSelectors.getBatchDetailItemReceipt(state),
    fetchingItemReceipt: echeqMassManagementSelectors.getFetchingItemReceipt(state),
    fetchingPendingTicket: echeqMassManagementSelectors.getFetchingPendingTicket(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(EcheqsMassManageBatchDetailPage);
